import DropDownPicker from 'react-native-dropdown-picker';
import React, { forwardRef, useEffect, useImperativeHandle, useState, useRef } from 'react';
import styles from '@stylesheet';
import { CHANGE_MENU, CLOSE_MENU_WARPER, SKIP_ONBOARDING } from '@reduxLocal/actions/types';
import menuStates from '../Components/menuStates';
import { useDispatch, useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { View, Pressable, Text, FlatList, Platform, Modal } from 'react-native';
import * as Animatable from 'react-native-animatable';
import { LinearGradient } from 'expo-linear-gradient';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useAlertDialog } from '@components/Global/AlertDialog/useAlertDialog';
import apis from '@api/api';

export type MenuWarperHandle = {
  toggleVisibility: () => void;
  animateOut: () => void;
};

// Menu Item Component with hover state
const MenuItem = ({ item, theme, onPress }: { item: any; theme: boolean; onPress: () => void }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Pressable
      style={{ minHeight: 30, maxHeight: 30, maxWidth: "100%" }}
      onPress={onPress}
      {...(Platform.OS === 'web' && {
        onHoverIn: () => setIsHovered(true),
        onHoverOut: () => setIsHovered(false),
      })}
    >
      <Animatable.View useNativeDriver transition={'backgroundColor'} style={{ flex: 1, flexDirection: 'row', backgroundColor: isHovered ? (theme ? "rgba(200, 162, 200, 0.3)" : "rgba(135, 206, 235, 0.3)") : "transparent", borderRadius: 10, padding: 10, minHeight: 30, maxHeight: 30, maxWidth: "100%" }}>
        <Text allowFontScaling={false} style={{ minHeight: 20, top: -7, color: theme ? '#1e293b' : '#f1f5f9', textAlign: 'left', fontSize: 14, fontWeight: '600' }}>{item.label}</Text>
      </Animatable.View>
    </Pressable>
  );
};

export const MenuWarper = forwardRef<MenuWarperHandle>((props, ref) => {
  const [items, setItems] = useState(menuStates);
  const [showSelector, setShowSelector] = useState(false);
  const animRef = useRef<any>(null);
  const menuWarperRef = useRef<View>(null);
  const dispatch = useDispatch();
  const theme = themeSelector();
  const learningMode = useSelector((state: DefaultRootState) => state.app.learningMode);
  const currentMenuType = useSelector((state: DefaultRootState) => state.navigation.menuType);
  const onboardingInProgress = useSelector((state: DefaultRootState) => state.UI.onboardingInProgress);
  const { showConfirm } = useAlertDialog();

  // Handle click outside to close
  const handleClickOutside = React.useCallback(() => {
    if (showSelector) {
      animRef.current?.fadeOutLeft?.();
      setTimeout(() => { setShowSelector(false) }, 350);
    }
  }, [showSelector]);

  // Web-specific click outside handler - screen wide
  useEffect(() => {
    if (Platform.OS === 'web' && showSelector) {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        // Check if the click is outside the menu warper and not on menu trigger elements
        if (!target.closest('[data-menu-warper="true"]') && 
            !target.closest('[data-menu-trigger]') &&
            !target.closest('.menu-toggle-button')) {
          animRef.current?.fadeOutLeft?.();
          setTimeout(() => { setShowSelector(false) }, 350);
        }
      };

      // Add listener to the document to capture all clicks screen-wide
      document.addEventListener('click', handleClickOutside, true);
      return () => {
        document.removeEventListener('click', handleClickOutside, true);
      };
    }
  }, [showSelector]);

  useImperativeHandle(ref, () => {
    return {
      toggleVisibility: async () => {
        // Prevent opening during onboarding
        if (!showSelector && onboardingInProgress) {
          const result = await showConfirm(
            'Skip Onboarding?',
            'You are currently in the onboarding process. Changing the menu will skip the onboarding. Do you want to continue?',
            {
              cancelText: 'Cancel',
              confirmText: 'Skip & Continue'
            }
          );
          
          if (result !== true) {
            return;
          }

          // Update backend to mark tutorial modal as complete
          try {
            await apis.setTutorial();
          } catch (error) {
            console.error('Error setting tutorial complete:', error);
            // Continue even if API call fails - still update Redux
          }

          // Update Redux state
          dispatch({ type: SKIP_ONBOARDING });
        }
        
        if(showSelector) {
          animRef.current?.fadeOutLeft?.();
          setTimeout(() => { setShowSelector(false) }, 350)
        } else {
          setShowSelector(true);
          animRef.current?.fadeInLeft?.();
        }
      },
      animateOut: () => {
        animRef.current?.fadeOutLeft?.();
        setTimeout(() => { setShowSelector(false) }, 350);
      }
    }
  }, [showSelector, onboardingInProgress, showConfirm, dispatch]);


  return showSelector && (
    <>
      {/* Native mobile: Full screen overlay to handle outside touches */}
      {Platform.OS !== 'web' && (
        <Pressable onPress={handleClickOutside}>
          <View style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            width: '100%',
            height: '100%',
            zIndex: 5,
            backgroundColor: 'transparent'
          }} />
        </Pressable>
      )}
      
      <LinearGradient
        colors={['#C8A2C8', '#87CEEB']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={{
          position: Platform.OS === 'web' ? 'absolute' as any : 'relative',
          borderBottomEndRadius: 20, 
          borderTopEndRadius: 20, 
          shadowRadius: 20, 
          shadowOpacity: 0.2, 
          flex: Platform.OS === 'web' ? 0.0000001 : 0.00001, 
          minWidth: 250, 
          top: Platform.OS === 'web' ? '15.5vh' as any : 25,
          alignSelf: 'stretch', 
          left: -4, 
          paddingHorizontal: 10, 
          minHeight: Platform.OS === 'web' ? 250 : 285,
          zIndex: Platform.OS === 'web' ? 10 : -1,
          overflow: 'hidden',
        }}
      >
        {/* Floating blur elements for depth */}
        {Platform.OS === 'web' && (
          <>
            <View style={{
              position: 'absolute',
              top: '10%',
              left: '5%',
              width: 60,
              height: 60,
              backgroundColor: theme ? 'rgba(59, 130, 246, 0.2)' : 'rgba(147, 197, 253, 0.15)',
              borderRadius: 30,
              ...(Platform.OS === 'web' && {
                filter: 'blur(40px)',
              }),
              zIndex: 0,
            }} />
            <View style={{
              position: 'absolute',
              bottom: '15%',
              right: '8%',
              width: 80,
              height: 80,
              backgroundColor: theme ? 'rgba(200, 162, 200, 0.25)' : 'rgba(135, 206, 235, 0.2)',
              borderRadius: 40,
              ...(Platform.OS === 'web' && {
                filter: 'blur(50px)',
              }),
              zIndex: 0,
            }} />
          </>
        )}
        <Animatable.View
          useNativeDriver
          ref={animRef}
          // @ts-ignore - Add data attribute for web click detection
          {...(Platform.OS === 'web' && { 'data-menu-warper': 'true' })}
          animation={'fadeInLeft'}
          duration={500}
          style={{
            flex: 1,
            backgroundColor: theme ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
            ...(Platform.OS === 'web' && {
              backdropFilter: 'blur(20px)',
            }),
            borderRadius: 20,
            paddingHorizontal: 10,
            zIndex: 1,
            overflow: 'visible',
          }}
        >
          <FlatList
            data={menuStates}
            style={{ flex: 1.5, alignSelf: "stretch", minHeight: 220 }}
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ flex: 1, paddingTop: Platform.select({ web: 20, default: 20 }), top: -10, paddingBottom: Platform.select({ web: undefined, default: 100 }), alignSelf: 'stretch', gap: 10 }}
            ItemSeparatorComponent={() => <View style={{ flex: 1, minHeight: 1, backgroundColor: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.2)' }} />}
            renderItem={({ item }) => {
              // Disable menu change if in learning mode and trying to change from 'learning'
              const isDisabled = learningMode && item.value !== 'learning';
              const isCurrentMenu = currentMenuType === item.value;
              
              return (
                <MenuItem
                  item={item}
                  theme={theme}
                  onPress={async () => {
                    // Prevent menu change if in learning mode
                    if (learningMode && item.value !== 'learning') {
                      console.log('🎓 Learning mode active - menu locked to "learning"');
                      setShowSelector(false);
                      return;
                    }
                    
                    // Check onboarding if menu is changing
                    if (onboardingInProgress && currentMenuType !== item.value) {
                      const result = await showConfirm(
                        'Skip Onboarding?',
                        'You are currently in the onboarding process. Changing the menu will skip the onboarding. Do you want to continue?',
                        {
                          cancelText: 'Cancel',
                          confirmText: 'Skip & Change Menu'
                        }
                      );
                      
                      if (result !== true) {
                        return;
                      }
                      
                      // Update backend to mark tutorial modal as complete
                      try {
                        await apis.setTutorial();
                      } catch (error) {
                        console.error('Error setting tutorial complete:', error);
                        // Continue even if API call fails - still update Redux
                      }

                      // Update Redux state
                      dispatch({ type: SKIP_ONBOARDING });
                    }

                    setTimeout(() => {
                      setShowSelector(false);
                      // Include learningMode in payload so reducer can check it
                      dispatch({ type: CHANGE_MENU, payload: item.value, learningMode });
                      setShowSelector(false)
                    }, 350)
                  }}
                />
              )
            }} />
          {/* Learning Mode Indicator */}
          {learningMode && (
            <View style={{
              padding: 12,
              marginTop: 10,
              backgroundColor: theme ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.2)',
              borderRadius: 8,
              borderWidth: 1,
              borderColor: theme ? '#3B82F6' : '#60A5FA',
            }}>
              <Text style={{
                fontSize: 12,
                color: theme ? '#1F2937' : '#F9FAFB',
                fontWeight: '600',
                textAlign: 'center',
              }}>
                🎓 Learning Mode: Menu locked to "Learning"
              </Text>
            </View>
          )}
        </Animatable.View>
      </LinearGradient>
    </>
  )
})

export default MenuWarper;